/**
 * useEdgeSwipe
 *
 * Captures horizontal swipe gestures that start within the left edge of the
 * viewport (e.g. the first ~24px). The browser's native back-navigation
 * gesture (Chrome on Android, Safari on iOS) uses the exact same zone, so
 * when we detect a horizontal-right swipe in that zone we call
 * `preventDefault()` on the `touchmove` event — the browser then drops its
 * back-nav and our `onSwipe` callback fires instead.
 *
 * On the PWA this is wired to open the side menu drawer on mobile; on
 * desktop we don't enable it because the menu is a permanent sidebar and
 * the gesture would feel intrusive.
 *
 * Design notes:
 * - We attach listeners to `window`, not to a specific element. The left
 *   edge zone is part of the viewport, not of any DOM subtree.
 * - We require `passive: false` on `touchmove` so we can call
 *   `preventDefault()`. Without it Chrome ignores the call (the listener
 *   is registered as passive by default for performance) and the browser
 *   fires its back gesture anyway.
 * - We only react to *single-finger* swipes. A multi-finger touch (pinch,
 *   two-finger scroll) must fall through to the page untouched.
 * - We require movement to be *clearly horizontal*: `deltaX` must exceed
 *   `deltaY` by a comfortable margin (and `deltaY` must stay under a hard
 *   cap). This filters out vertical scrolls that happen to start at the
 *   edge, e.g. swiping down on the AppBar to scroll the page.
 * - We track one gesture per touchstart. On `touchend` / `touchcancel` we
 *   reset state so the next touch is evaluated independently.
 * - We deliberately do NOT call `preventDefault()` on `touchstart`. If we
 *   did, we would block the browser from ever seeing the gesture and we
 *   could not distinguish a "swipe right" from a "swipe down" that just
 *   happens to start at the edge. Letting `touchstart` propagate and only
 *   calling `preventDefault()` on `touchmove` keeps the page scroll alive
 *   for vertical gestures and only cancels horizontal ones.
 */

import { useEffect, useRef } from 'react';

export interface UseEdgeSwipeOptions {
  /** When false, the hook is inert — no listeners are attached. */
  enabled: boolean;
  /** Fired exactly once per detected edge swipe. */
  onSwipe: () => void;
  /** Width of the left edge capture zone in CSS pixels. Default 24 —
   *  matches the browser's native back-gesture zone on iOS/Android. */
  edgeWidth?: number;
  /** Minimum horizontal distance (CSS px) the finger must travel before
   *  the swipe is considered intentional. Default 40 — large enough to
   *  filter taps and tiny jitters, small enough to feel responsive. */
  threshold?: number;
}

const DEFAULT_EDGE_WIDTH = 24;
const DEFAULT_THRESHOLD = 40;
/** Vertical movement beyond this is treated as a scroll, not a swipe. */
const VERTICAL_CAP = 24;
/** Horizontal must exceed vertical by this margin to count as a swipe. */
const HORIZONTAL_DOMINANCE = 1.5;

export function useEdgeSwipe({
  enabled,
  onSwipe,
  edgeWidth = DEFAULT_EDGE_WIDTH,
  threshold = DEFAULT_THRESHOLD,
}: UseEdgeSwipeOptions): void {
  // Keep latest callbacks in a ref so the listeners we register (with empty
  // deps) always read fresh values without forcing a re-attach on every
  // render. Same pattern as `useTouchInput` — sync the ref inside an
  // effect so the assignment never happens during render (the
  // react-hooks/refs rule forbids in-render ref mutation).
  const latestRef = useRef({ onSwipe });
  useEffect(() => {
    latestRef.current.onSwipe = onSwipe;
  }, [onSwipe]);

  useEffect(() => {
    if (!enabled) return;
    if (typeof window === 'undefined') return;

    let tracking = false;
    let startX = 0;
    let startY = 0;
    let fired = false;

    const handleTouchStart = (e: TouchEvent) => {
      // Only react to a single finger — multi-finger gestures (pinch,
      // two-finger scroll) must reach the page untouched.
      if (e.touches.length !== 1) {
        tracking = false;
        return;
      }
      const t = e.touches[0];
      if (!t) return;
      // Only the left edge counts. Anywhere else we ignore.
      if (t.clientX > edgeWidth) {
        tracking = false;
        return;
      }
      tracking = true;
      fired = false;
      startX = t.clientX;
      startY = t.clientY;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!tracking || fired) return;
      const t = e.touches[0];
      if (!t) return;

      const deltaX = t.clientX - startX;
      const deltaY = Math.abs(t.clientY - startY);

      // Filter out vertical-dominant gestures (page scrolls that happen
      // to start at the edge).
      if (deltaY > VERTICAL_CAP) return;
      if (deltaX <= 0) return; // finger moved left — not a "open menu" swipe
      if (deltaX < deltaY * HORIZONTAL_DOMINANCE) return; // not horizontal enough

      if (deltaX >= threshold) {
        // This is the move we want to steal from the browser. Cancel its
        // default action (back-nav) and notify our owner. `fired` latches
        // so we don't fire `onSwipe` multiple times for a single gesture.
        e.preventDefault();
        fired = true;
        latestRef.current.onSwipe();
      }
    };

    const handleTouchEnd = () => {
      tracking = false;
      fired = false;
    };

    // `passive: false` is mandatory on touchmove — without it, the browser
    // treats the listener as performance-critical and ignores
    // preventDefault(), which means the back-nav fires anyway and our
    // hook becomes a no-op.
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleTouchEnd, { passive: true });
    window.addEventListener('touchcancel', handleTouchEnd, { passive: true });

    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('touchcancel', handleTouchEnd);
    };
    // `edgeWidth` and `threshold` are baked into the closure. Callers
    // shouldn't be changing them at runtime — the defaults are tuned for
    // the browser's native gesture. `onSwipe` is read through the latestRef
    // closure, so changes to it do not require a re-attach.
  }, [enabled, edgeWidth, threshold]);
}
